import React from 'react';
import { Calendar, MapPin, Users, Trophy, Rocket, Target, Briefcase, BarChart3, Activity } from 'lucide-react';

const TimelinePage = () => {
  const timelineEvents = [
    {
      year: '2025',
      title: 'Company Founded',
      description:
        "ExessD was founded with a vision to partner with global software organizations in their business transformation journey, harnessing the immense potential of India’s rich talent pool. Our mission is to empower companies to efficiently build, manage, and scale high-performing teams.",
      icon: <Rocket className="text-blue-600" size={24} />,
      color: 'blue',
      details: ['Founded by experienced tech professionals', 'Initial team of 5 engineers'],
    },
  ];

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 via-blue-800 to-purple-700 text-white py-20 md:py-32">
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl">
            <div className="mb-6">
              <span className="inline-block bg-yellow-400 text-black px-4 py-2 rounded-md text-sm font-bold uppercase tracking-wide">
                Timeline
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">Our Journey</h1>
            <p className="text-xl md:text-2xl mb-8 leading-relaxed opacity-90">
              A detailed timeline of our growth, milestones, and achievements since our founding.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-300" />
              {timelineEvents.map((event, index) => (
                <div key={index} className="relative flex items-start mb-12">
                  {/* Timeline dot */}
                  <div
                    className={`flex-shrink-0 w-16 h-16 bg-${event.color}-100 rounded-full flex items-center justify-center border-4 border-white shadow-lg relative z-10`}
                  >
                    {event.icon}
                  </div>

                  {/* Content */}
                  <div className="ml-8 flex-grow">
                    <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                          <Calendar className="text-gray-500 mr-2" size={16} />
                          <span className="text-sm text-gray-500">
                            {event.month ? `${event.month} ` : ''}{event.year}
                          </span>
                        </div>
                        <span
                          className={`px-3 py-1 bg-${event.color}-100 text-${event.color}-800 text-xs font-semibold rounded-full`}
                        >
                          Milestone
                        </span>
                      </div>

                      <h3 className="text-xl font-bold mb-3 text-red-600">{event.title}</h3>
                      <p className="text-gray-700 mb-4">{event.description}</p>

                      <div className="space-y-2">
                        {event.details.map((detail, detailIndex) => (
                          <div key={detailIndex} className="flex items-center">
                            <div className={`w-2 h-2 bg-${event.color}-600 rounded-full mr-3`} />
                            <span className="text-sm text-gray-600">{detail}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits (cards use greenish hero tone) */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-red-600">Benefits</h2>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="rounded-xl p-8 shadow-lg bg-green-900 text-white">
              <div className="mb-6">
                <Briefcase size={36} className="opacity-90" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Greater Business Goals Alignment</h3>
              <p className="text-white/80 leading-relaxed">
                Align recruitment efforts with your unique requirements, driving overall company success and growth.
              </p>
            </div>

            {/* Card 2 */}
            <div className="rounded-xl p-8 shadow-lg bg-green-900 text-white">
              <div className="mb-6">
                <BarChart3 size={36} className="opacity-90" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Maximize Employee Engagement</h3>
              <p className="text-white/80 leading-relaxed">
                Align performance management with operations to drive employee engagement and operational success.
              </p>
            </div>

            {/* Card 3 */}
            <div className="rounded-xl p-8 shadow-lg bg-green-900 text-white">
              <div className="mb-6">
                <Activity size={36} className="opacity-90" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Increase Recruiter Productivity</h3>
              <p className="text-white/80 leading-relaxed">
                Enhance productivity with our integrated solution, encompassing payroll, HR, background checks, and engagement.
              </p>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default TimelinePage;
